import { Center } from "@freightsimple/ui-core/PageLayout/Center";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { TrackingGroupShipment } from "@freightsimple/generated-dashboard-openapi-client/models/TrackingGroupShipment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { LoadFailed } from "@/Shared/Components/LoadFailed";
import { isNotFoundError } from "@/Helpers/isNotFoundError";
import ErrorAnimation from "@/Shared/Icons/ErrorAnimation";
import { useOnce } from "@/Hooks/useOnce";
import { useTrackingService } from "@/Services/TrackingService";
import { ShipmentsMap } from "@/Shared/Maps/ShipmentsMap";
import { TrackingOverlapCard } from "./CustomComponents/Internal/TrackingOverlapCard";
import { TrackingShipmentsList } from "./CustomComponents/TrackingShipmentsList";

interface TrackingShipmentsScreenProps {
  packedGroup: string;
}

export function TrackingShipmentsScreen(props: TrackingShipmentsScreenProps) {
  const { packedGroup } = props;
  const trackingService = useTrackingService();
  const navigate = useNavigate();

  const [shipments, setShipments] = useState<TrackingGroupShipment[]>();
  const [notFound, setNotFound] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [highlightedLaneIndex, setHighlightedLaneIndex] = useState<number>();

  async function loadShipments() {
    setLoadFailed(false);
    try {
      setShipments(await trackingService.getTrackingGroup(packedGroup));
    } catch (error) {
      if (isNotFoundError(error)) {
        setNotFound(true);
      } else {
        console.warn("Error loading tracking group:", { error });
        setLoadFailed(true);
      }
    }
  }

  useOnce(loadShipments);

  if (notFound) {
    return (
      <Center minHeight="calc(100vh - 140px)">
        <Stack gap={5} align="center">
          <Stack gap={0} align="center">
            <Heading level={2}>Shipments Not Found</Heading>
            <T size="base" color="secondary" align="center">
              Please verify the tracking link and try again.
            </T>
          </Stack>
          <ErrorAnimation />
        </Stack>
      </Center>
    );
  }

  if (loadFailed) {
    return <LoadFailed onRetry={loadShipments} />;
  }

  if (shipments === undefined) {
    return <PageLoadingSpinner />;
  }

  const lanes = shipments.map((shipment) => ({
    pickup: shipment.pickupLocation,
    delivery: shipment.deliveryLocation,
    shipmentState: shipment.shipmentState,
  }));

  return (
    <>
      <ShipmentsMap lanes={lanes} highlightedLaneIndex={highlightedLaneIndex} />
      <Stack gap={8} align="center">
        <TrackingOverlapCard width="list">
          <TrackingShipmentsList
            shipments={shipments}
            onSelectShipment={(trackingId) => {
              navigate(`/t?tId=${trackingId}`);
            }}
            onHighlightShipment={setHighlightedLaneIndex}
          />
        </TrackingOverlapCard>
      </Stack>
    </>
  );
}
